<template>
  <div>
    <div class="card-header">
      <h3>{{ $t("resultsUploadHistory") }}</h3>
    </div>
    <div class="card-body">
      <!--==================================-->
      <div class="dextop-table-view">
        <div class="table-responsive api-keys-table">
          <!-- table -->
          <vue-good-table
            styleClass="vgt-table striped"
            :columns="columns"
            :fixed-header="false"
            :rows="rows"
            :search-options="{
              enabled: true,
              externalQuery: searchTerm,
              trigger: 'enter',
            }"
            :pagination-options="{
              enabled: false,
              perPage: pageLength,
            }"
            mode="remote"
            @on-page-change="onPageChange"
            @on-sort-change="onSortChange"
            @on-per-page-change="onPerPageChange"
          >
          <div slot="emptystate" style="text-align: center">
            {{ $t("noDataForTable") }}
          </div>
            <template slot="table-row" slot-scope="props">
              <!-- Column: Name -->
              <span v-if="props.column.field === 'createdAt'">
                <p class="">
                  {{ $dateFormatter(props.row.createdAt, $i18n.locale) }}
                </p>
              </span>
              <span
                class="d-flex align-items-center justify-content-center gap-2"
                v-else-if="props.column.field === 'action'"
                ><div
                  class="cursor-pointer"
                  @click="downloadFiles([props.row.fileId])"
                >
                  <feather-icon size="16" icon="DownloadIcon" />
                </div>
                <div class="cursor-pointer" @click="destroy(props.row.id)">
                  <feather-icon size="16" icon="TrashIcon" />
                </div>
              </span>
              <!-- Column: Common -->
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <div class="custom-search">
            <b-form-group>
              <div class="d-flex align-items-center">
                <span class="text-nowrap"> {{ $t("rowsPerPage") }} </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['10', '25', '50', '100']"
                  class="ml-1"
                  @input="(value) => onPerPageChange({ pageLength: value })"
                />
              </div>
            </b-form-group>
          </div>
          <!-- pagination -->
          <b-pagination
            :value="1"
            :total-rows="totalRecords"
            :per-page="pageLength"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @input="(value) => onPageChange({ currentPage: value })"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </div>
      <div class="reponsive-view-cards">
        <div class="row">
          <div class="col-12" v-for="row in rows" :key="row.id">
            <div class="card mb-1">
              <div
                class="card-header d-flex align-items-center justify-content-between"
              >
                <h5 class="card-title">
                  {{ $dateFormatter(row.createdAt, $i18n.locale) }}
                </h5>
                <div class="d-flex gap-2">
                  <div
                    class="cursor-pointer"
                    @click="downloadFiles([row.fileId])"
                  >
                    <feather-icon size="18" icon="DownloadIcon" />
                  </div>
                  <div class="cursor-pointer" @click="destroy(row.id)">
                    <feather-icon size="18" icon="TrashIcon" />
                  </div>
                </div>
              </div>
              <div class="card-body pt-0">
                <ul class="card-list">
                  <li>
                    <div class="left">
                      <strong>{{ $t("type") }}: </strong>
                    </div>
                    <div class="right">
                      <span>{{ row.type }}</span>
                    </div>
                  </li>
                  <li>
                    <div class="left">
                      <strong>{{ $t("fileId") }}: </strong>
                    </div>
                    <div class="right">
                      <span>{{ row.fileId }}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="custom-search">
            <b-form-group>
              <div class="d-flex align-items-center">
                <span class="text-nowrap"> {{ $t("rowsPerPage") }} </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['10', '25', '50', '100']"
                  class="ml-1"
                  @input="(value) => onPerPageChange({ pageLength: value })"
                />
              </div>
            </b-form-group>
          </div>
          <b-pagination
            :value="1"
            :total-rows="totalRecords"
            :per-page="pageLength"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @input="(value) => onPageChange({ currentPage: value })"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </div>
      <!--==================================-->
    </div>
  </div>
</template>

<script>
import { debounce } from "@/utils/debounce";
import FileService from "@/services/file.service";

export default {
  computed: {
    columns() {
      return [
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("date"),
          field: "createdAt",
        },

        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("type"),
          field: "type",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("fileId"),
          field: "fileId",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("action"),
          field: "action",
          sortable: false,
        },
      ];
    },
  },
  data() {
    return {
      file: null,
      totalRecords: 0,
      pageLength: 10,
      page: 1,
      sortBy: "createdAt",
      sortOrder: "desc",
      rows: [],
      searchTerm: "",
    };
  },

  async created() {
    this.debouncedFetch = debounce(async (newValue, oldValue) => {
      try {
        await this.loadItems();
      } catch (e) {
        console.error(e);
      }
    }, 300);
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this, newProps);
    },
    downloadFiles(array) {
      FileService.downloadFiles(array, this.$store);
    },

    onPageChange(params) {
      this.page = params.currentPage;
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ pageLength: params.pageLength });
      this.loadItems();
    },

    onSortChange(params) {
      this.sortOrder = params[0].type;
      if (params[0].type == "none") this.sortOrder = "asc";
      this.sortBy = params[0].field;
      this.loadItems();
    },
    // load items is what brings back the rows from server
    async loadItems() {
      try {
        this.$store.commit("showLoader", true);
        let response = await this.$store.dispatch(
          "caseLists/getMultipleResults",
          {
            id: this.$route.params.id,
            data: {
              page: this.page,
              sortBy: this.sortBy,
              sortOrder: this.sortOrder,
              perPage: this.pageLength,
            },
          }
        );
        this.rows = response?.data?.data;
        this.totalRecords = response?.data?.total;
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
    async destroy(id) {
      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToDeleteThisRecord"),
        subText: this.$t("yesDeleteIt"),
      });

      if (confirmed) {
        try {
          this.$store.commit("showLoader", true);
          await this.$store.dispatch("caseLists/destroyResult", id);
          this.loadItems();
        } catch (e) {
          console.error("api error ___", e);
        } finally {
          this.$store.commit("showLoader", false);
        }
      }
    },
  },
};
</script>

<style>
#nprogress {
  position: relative;
  z-index: 9999999;
}

.white-color {
  color: white !important;
}

.vgt-responsive {
  overflow-x: visible !important;
}
</style>
